import { buildFormData } from '../../design-system/utils';
import { getEnvRequired } from '../../shared/utilities/env';
import { ApiClient } from '../shared/api/client';

import {
    serializeContractFromAPI,
    serializeLegalInformationFromAPI,
    serializeProofDocumentFromAPI,
    serializeQuotePricing,
} from './serializers';
import {
    ContractId,
    LegalInformationResponseFromAPI,
    PostContractBody,
    ProofDocumentFromAPI,
    QuotePricingPayloadForAPI,
    QuotePricingResponseFromAPI,
    PaymentScheduleBody,
    InstalmentBody,
    ContractResponseFromAPI,
    ExistingPaymentSchedule,
} from './types';

const CONTRACT_API_URL = getEnvRequired('CONTRACT_API_URL');
const APIV2_URL = getEnvRequired('APIV2_URL');
const REQUEST_MANAGER_API_URL = getEnvRequired('REQUEST_MANAGER_API_URL');

const requestManagerApiClient = new ApiClient(true);
const apiClient = new ApiClient(false);

export async function fetchContract(contractId: ContractId) {
    const url = `${CONTRACT_API_URL}/contract/${contractId}`;

    const response = await apiClient.get<ContractResponseFromAPI>(url).catch(() => null);

    if (response === null) {
        return null;
    }

    return serializeContractFromAPI(response);
}

export async function postContract(contractId: ContractId, contract: PostContractBody) {
    const url = `${REQUEST_MANAGER_API_URL}/contract/${contractId}`;
    const formData = buildFormData(contract);

    const response = await requestManagerApiClient.postForm<ContractResponseFromAPI>(url, formData);
    return serializeContractFromAPI(response);
}

export async function fetchLegalInformation(contractId: ContractId) {
    // we select only needed fields (json:api)
    const url = `${CONTRACT_API_URL}/dossier/${contractId}/legal-information?fields[legal_information]=reference,language`;

    const response = await apiClient.get<LegalInformationResponseFromAPI>(url);
    return serializeLegalInformationFromAPI(response);
}

export async function fetchQuotePricing(contractId: ContractId) {
    const url = `${APIV2_URL}/quotePricing/${contractId}`;

    const response = await apiClient.get<QuotePricingResponseFromAPI>(url).catch(() => null);
    if (response === null) {
        return null;
    }
    return serializeQuotePricing(response);
}

export async function uploadProofOfDocuments(contractId: ContractId, file: File) {
    const url = `${APIV2_URL}/quotePricing/upload-proof-document?quote_id=${contractId}`;
    const formData = buildFormData({ file });

    const response = await apiClient.postForm<ProofDocumentFromAPI>(url, formData);
    return serializeProofDocumentFromAPI(response);
}

export async function deleteProofOfDocuments(fileName: string) {
    const url = `${APIV2_URL}/quotePricing/delete-proof-document`;

    await apiClient.post(url, JSON.stringify({ fileName }));
    return { success: true };
}

export async function postChangeQuotePrices(quotePricingBody: QuotePricingPayloadForAPI) {
    const url = `${APIV2_URL}/quotePricing/changeQuotePrices`;

    const response = await apiClient.post<QuotePricingResponseFromAPI>(
        url,
        JSON.stringify(quotePricingBody),
    );
    return serializeQuotePricing(response);
}

export async function postPaymentSchedule(
    contractId: ContractId,
    paymentSchedule: PaymentScheduleBody,
) {
    const url = `${CONTRACT_API_URL}/contract/${contractId}/payment-schedule`;

    const response = await apiClient.post<ContractResponseFromAPI>(
        url,
        JSON.stringify(paymentSchedule),
    );
    const contract = serializeContractFromAPI(response);
    return contract.paymentSchedule as ExistingPaymentSchedule;
}

export async function putPaymentSchedule(
    contractId: ContractId,
    paymentSchedule: PaymentScheduleBody,
) {
    const url = `${CONTRACT_API_URL}/contract/${contractId}/payment-schedule`;

    const response = await apiClient.put<ContractResponseFromAPI>(
        url,
        JSON.stringify(paymentSchedule),
    );
    const contract = serializeContractFromAPI(response);
    return contract.paymentSchedule as ExistingPaymentSchedule;
}

export async function postInstalment(contractId: ContractId, instalment: InstalmentBody) {
    const url = `${CONTRACT_API_URL}/contract/${contractId}/payment-schedule/instalment`;

    const response = await apiClient.post<ContractResponseFromAPI>(url, JSON.stringify(instalment));
    const contract = serializeContractFromAPI(response);
    return contract.paymentSchedule as ExistingPaymentSchedule;
}

export async function putInstalment(
    contractId: ContractId,
    instalmentId: string,
    instalment: InstalmentBody,
) {
    const url = `${CONTRACT_API_URL}/contract/${contractId}/payment-schedule/instalment/${instalmentId}`;

    const response = await apiClient.put<ContractResponseFromAPI>(url, JSON.stringify(instalment));
    const contract = serializeContractFromAPI(response);
    return contract.paymentSchedule as ExistingPaymentSchedule;
}

export async function deleteInstalment(contractId: ContractId, instalmentId: string) {
    const url = `${CONTRACT_API_URL}/contract/${contractId}/payment-schedule/instalment/${instalmentId}`;

    const response = await apiClient.delete<ContractResponseFromAPI>(url);
    const contract = serializeContractFromAPI(response);
    return contract.paymentSchedule as ExistingPaymentSchedule;
}
